import { useNavigate, useSearch } from '@tanstack/react-router';
import * as React from 'react';

import type { SeniorityLevelDto } from '@/api/catalog-client';
import { CatalogSupplierClientProvider } from '@/api/catalog-client-context';
import type { CatalogReferentialSearch } from '@/routes/supplier/catalog/catalog-referential-model';
import { validateCatalogReferentialSearch } from '@/routes/supplier/catalog/catalog-referential-route';
import {
  SupplierCampaignsScreen,
  type SupplierCampaignsStatut,
} from '@/routes/supplier/campaigns/supplier-campaigns-screen';

// =============================================================================
// supplier-campaigns-route.tsx — Adaptateur routeur de « Mes campagnes en cours » (GATE_HUMAN 2026-09-11).
// =============================================================================
// Route `/campagnes` (monde FOURNISSEUR). Le BC catalog AUTO-PORTE son DI seam : la route enveloppe l'écran
// avec le CatalogSupplierClientProvider local (comme /catalog) — le périmètre métier réutilise le référentiel
// catalogue (décision GATE 5). L'ÉTAT DE VUE est porté par l'URL (search params TanStack ; RULE_REF_006 :
// jamais localStorage) :
//   • `statut` = pré-filtre En cours (défaut, non sérialisé) / Archivés (`?statut=archive`) — esprit des 2
//     entrées de menu à venir ;
//   • `campaign` = campagne sélectionnée (détail affiché) ;
//   • `famille`/`sousFamille`/`niveau`/`q`/`profil` = état de vue du CATALOGUE de périmètre (réutilisé tel
//     quel du référentiel fournisseur).
// L'écran reste CONTRÔLÉ et testable hors routeur.

/** Forme validée du search de `/campagnes` : état catalogue + `statut` + `campaign` sélectionnée. */
export interface SupplierCampaignsSearch extends CatalogReferentialSearch {
  /** Pré-filtre de statut ; seule la valeur `archive` est sérialisée (« en-cours » = défaut implicite). */
  readonly statut?: 'archive';
  /** Campagne sélectionnée (détail). */
  readonly campaign?: string;
}

/**
 * Valide/normalise les search params de `/campagnes`. Réutilise `validateCatalogReferentialSearch` pour les
 * 5 params catalogue (parité stricte de l'état de vue), puis ajoute `statut` (allowlist {archive}) et
 * `campaign` (string non vide). Une valeur absente/invalide n'est PAS sérialisée (URL propre).
 */
export function validateSupplierCampaignsSearch(
  search: Record<string, unknown>,
): SupplierCampaignsSearch {
  const result: {
    famille?: string;
    sousFamille?: string;
    niveau?: SeniorityLevelDto;
    q?: string;
    profil?: string;
    statut?: 'archive';
    campaign?: string;
  } = { ...validateCatalogReferentialSearch(search) };

  const statut = search['statut'];
  if (statut === 'archive') {
    result.statut = 'archive';
  }
  const campaign = search['campaign'];
  if (typeof campaign === 'string' && campaign.length > 0) {
    result.campaign = campaign;
  }
  return result;
}

/** Compose l'objet de search complet (compacté) à partir de l'état catalogue + statut + campagne. */
function composeSearch(
  catalog: CatalogReferentialSearch,
  statut: SupplierCampaignsStatut,
  campaign: string | undefined,
): SupplierCampaignsSearch {
  const out: {
    famille?: string;
    sousFamille?: string;
    niveau?: SeniorityLevelDto;
    q?: string;
    profil?: string;
    statut?: 'archive';
    campaign?: string;
  } = {};
  if (catalog.famille !== undefined) out.famille = catalog.famille;
  if (catalog.sousFamille !== undefined) out.sousFamille = catalog.sousFamille;
  if (catalog.niveau !== undefined) out.niveau = catalog.niveau;
  if (catalog.q !== undefined && catalog.q.length > 0) out.q = catalog.q;
  if (catalog.profil !== undefined) out.profil = catalog.profil;
  if (statut === 'archive') out.statut = 'archive';
  if (campaign !== undefined) out.campaign = campaign;
  return out;
}

export function SupplierCampaignsRoute(): React.JSX.Element {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as SupplierCampaignsSearch;

  const statut: SupplierCampaignsStatut =
    search.statut === 'archive' ? 'archive' : 'en-cours';
  // Sous-ensemble CATALOGUE de l'état d'URL (réutilise la garde de type du référentiel).
  const catalogSearch = validateCatalogReferentialSearch(
    search as unknown as Record<string, unknown>,
  );

  const onChangeStatut = React.useCallback(
    (next: SupplierCampaignsStatut): void => {
      void navigate({
        to: '/campagnes',
        search: composeSearch(catalogSearch, next, search.campaign),
      });
    },
    [navigate, catalogSearch, search.campaign],
  );

  // Changer de campagne RÉINITIALISE l'état de vue catalogue (le périmètre change : famille/profil obsolètes).
  const onSelectCampaign = React.useCallback(
    (campaignId: string | undefined): void => {
      void navigate({
        to: '/campagnes',
        search: composeSearch({}, statut, campaignId),
      });
    },
    [navigate, statut],
  );

  const onChangeCatalogSearch = React.useCallback(
    (next: CatalogReferentialSearch): void => {
      void navigate({
        to: '/campagnes',
        search: composeSearch(next, statut, search.campaign),
      });
    },
    [navigate, statut, search.campaign],
  );

  // PHASE C (GATE_HUMAN 2026-09-12) : le deep-link vers `/saisie/$submissionId` depuis le détail campagne
  // est RETIRÉ — la grille TJM est désormais embarquée dans le détail (composant réutilisable). Le dashboard
  // conserve, lui, son CTA « Reprendre ma saisie » vers la route `/saisie` (route préservée).

  return (
    <CatalogSupplierClientProvider>
      <SupplierCampaignsScreen
        statut={statut}
        onChangeStatut={onChangeStatut}
        selectedCampaignId={search.campaign}
        onSelectCampaign={onSelectCampaign}
        catalogSearch={catalogSearch}
        onChangeCatalogSearch={onChangeCatalogSearch}
      />
    </CatalogSupplierClientProvider>
  );
}
